import {
  isDIF,
  isEmpty,
  isEnFace,
  isExcision,
  isMultipleShaves,
  isNail,
  isOrientedEllipse,
  isHalves,
  isPunch,
  isShave,
  isSkin,
  isSlowMohs,
  isSlowMohsDebulk,
  isSoftTissue,
} from "./conditions";
import { GrossingSelections } from "./selections";

// GrossingModifiers are switches which the user can toggle to affect the overall structure of the Grossing Mad-Lib.
//
// label       - The label displayed on the button for this GrossingModifier.
// condition?  - A GrossingConditional which must return true for this GrossingModifer to be available.
// turnsOff?   - An array of other GrossingModifiers which will be deselected when this GrossingModifier is selected.

export type GrossingModifier = {
  label: string;
  condition?: (selections: GrossingSelections) => boolean;
  turnsOff?: Array<keyof typeof grossingModifiers>;
  turnsOn?: Array<keyof typeof grossingModifiers>;
};

export const grossingModifiers: { [key: string]: GrossingModifier } = {
  multipleShaves: {
    label: "Multiple Shaves",
    condition: isShave,
    turnsOff: ["shaveFragments", "dif", "lipoma"],
  },
  shaveFragments: {
    label: "Multiple Fragments",
    condition: isShave,
    turnsOff: ["multipleShaves", "dif", "lipoma"],
  },
  subcutaneous: {
    label: "Subcutaneous Nodule",
    condition: isExcision,
  },
  withA: {
    label: "with a...",
    condition: (s) => !isNail(s) && !isSlowMohs(s),
    turnsOff: ["dif"],
  },
  cyst: {
    label: "Cyst",
    condition: (s) => isExcision(s) || isPunch(s),
    turnsOn: ["measurement"],
  },
  tips: {
    label: "Tips",
    condition: isExcision,
  },
  oriented: {
    label: "Oriented Ellipse",
    condition: (s) => isExcision(s),
  },
  "additional-orienting-mark": {
    label: "Additional Orienting Mark",
    condition: (s) => isOrientedEllipse(s),
  },
  ellipse: {
    label: "Ellipse",
    condition: (s) => isExcision(s) && !isOrientedEllipse(s),
  },
  halves: {
    label: "Halves",
    condition: isOrientedEllipse,
  },
  "en-face": {
    label: "En-Face",
    condition: (s) => isOrientedEllipse(s) && !isHalves(s),
  },
  "oriented-submission": {
    label: "Oriented Submission",
    condition: (s) => isOrientedEllipse(s) && !isEnFace(s),
  },
  small: {
    label: "Small",
    condition: (s) => isShave(s) || isPunch(s) || isExcision(s) || isNail(s),
    turnsOff: ["dif"],
  },
  softened: {
    label: "Softened",
    turnsOff: ["dif"],
  },
  decalcified: {
    label: "Decalcified",
    turnsOff: ["dif"],
  },
  singleCassette: {
    label: "Single Cassette",
    condition: isMultipleShaves,
  },
  lipoma: {
    label: "Lipoma",
    condition: (s) =>
      (isSoftTissue(s) || isSkin(s)) &&
      !s.modifierSelections.shaveFragments &&
      !s.modifierSelections.multipleShaves &&
      !s.modifierSelections.dif,
    turnsOff: ["shaveFragments", "multipleShaves", "dif"],
  },
  skinAttached: {
    label: "Skin Attached",
    condition: isSoftTissue,
  },
  slowMohsMeasurements: {
    label: "Measurements",
    condition: isSlowMohs,
  },
  slowMohsOrientingMark: {
    label: "Orienting Mark",
    condition: isSlowMohs,
  },
  slowMohsAdditionalOrientingMark: {
    label: "Additional Orienting Mark",
    condition: (s) =>
      isSlowMohsDebulk(s) && s.modifierSelections.slowMohsOrientingMark,
  },
  empty: {
    label: "Empty",
    turnsOff: ["dif"],
  },
  filterBag: {
    label: "Filter Bag",
    condition: (s) => isEmpty(s),
  },
  cellBlock: {
    label: "Cell-Block",
    condition: (s) => isEmpty(s),
  },
  falseMargins: {
    label: "False Margins",
    condition: (s) => !isSlowMohs(s),
    turnsOff: ["dif"],
  },
  foreignBody: {
    label: "Foreign Body",
    condition: (s) => !isSlowMohs(s),
    turnsOff: ["dif"],
  },
  eyeWedge: {
    label: "Eye Wedge",
    condition: (s) => isExcision(s) && isSkin(s),
  },
  measurement: {
    label: "Measurement",
    condition: (s) =>
      (isPunch(s) || isExcision(s)) && s.modifierSelections.cyst,
  },
  dif: {
    label: "DIF",
    condition: (s) => isDIF(s),
    turnsOff: [
      "multipleShaves",
      "shaveFragments",
      "withA",
      "small",
      "softened",
      "decalcified",
      "foreignBody",
      "falseMargins",
      "empty",
      "filterBag",
      "cellBlock",
      "lipoma",
    ],
  },
};
